// SF1–SF3 — Sales by State, City and Pincode.
//
// One component at three grains, not three tables. A row drills into its own
// children — a state opens that state's cities, a city opens its pincodes — so
// navigating narrows rather than re-ranking the country.
//
// Share is the share of NATIONAL revenue at every level, including inside a
// drill. That is deliberate: a city reading 15% means 15% of the business, and
// it would be a different number every time you navigated if it meant 15% of
// whichever parent you happened to be inside. The local ratio is the subtotal
// in the breadcrumb instead.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronRight, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../GeographicSales.module.css';
import { getRegions } from '../services/geographicSalesApi';
import SyncProgress from '../../components/SyncProgress';

const GRAINS = [
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'pincode', label: 'Pincode' },
];

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const money = (value) =>
  value === null || value === undefined ? <Blank /> : `₹${inr.format(Math.round(value))}`;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

// Absent below the volume floor, never zero — a region with too little history
// has no growth figure rather than a flat one.
const Growth = ({ value }) => {
  if (value === null || value === undefined) return <Blank />;
  const up = value > 0;
  return (
    <span className={up ? local.changeUp : local.changeDown}>
      {up ? '+' : ''}{(value * 100).toFixed(1)}%
    </span>
  );
};

// How much of a region is wholesale. A region can rank high on one bulk
// account, which is a different business from broad retail demand, so the split
// is shown rather than left for the seller to guess at.
const Wholesale = ({ value }) => {
  if (!value) return <Blank />;
  return (
    <span className={value >= 0.5 ? local.wholesaleHeavy : undefined}>
      {(value * 100).toFixed(0)}%
    </span>
  );
};

const Share = ({ value }) => (
  <>
    <span className={local.shareBar} style={{ width: `${(value || 0) * 100}%` }} />
    <span className={local.shareValue}>
      {value === null || value === undefined ? <Blank /> : `${(value * 100).toFixed(1)}%`}
    </span>
  </>
);

const RegionTable = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [grain, setGrain] = useState('state');
  const [scope, setScope] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getRegions(token, { start, end, grain, ...scope }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, grain, scope, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  // Drilling down a level, scoped to the row that was clicked.
  const drill = (row) => {
    if (row.unassigned) return;
    if (grain === 'state') {
      setScope({ state: row.region });
      setGrain('city');
    } else if (grain === 'city') {
      // City rows read "City, STATE"; the scope wants them apart.
      const [city, state] = row.region.split(', ');
      setScope({ state, city });
      setGrain('pincode');
    }
  };

  const goTo = (level) => {
    if (level === 'national') { setScope({}); setGrain('state'); }
    if (level === 'state') { setScope({ state: scope.state }); setGrain('city'); }
  };

  const rows = data?.rows || [];
  const canDrill = grain !== 'pincode';
  // The column earns its place only on accounts that actually sell B2B.
  const showWholesale = (data?.subtotal?.business_revenue || 0) > 0;
  const columns = showWholesale ? 8 : 7;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Where your sales come from</h2>
        <div className={local.segmented}>
          {GRAINS.map((option) => (
            <button key={option.key} type="button"
              className={option.key === grain ? local.segmentOn : local.segment}
              onClick={() => { setGrain(option.key); setScope({}); }}>
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <p className={local.muted}>
        Revenue here uses the same definition as the Sales Performance Dashboard
        and reconciles with it. Share is of national revenue at every level.
      </p>

      {/* The active path and its subtotal, always visible. */}
      <div className={local.breadcrumb}>
        <button type="button" className={scope.state ? local.crumb : local.crumbCurrent}
          onClick={() => goTo('national')}>India</button>
        {scope.state && (
          <>
            <span className={local.crumbSep}>›</span>
            <button type="button"
              className={scope.city ? local.crumb : local.crumbCurrent}
              onClick={() => goTo('state')}>{scope.state}</button>
          </>
        )}
        {scope.city && (
          <>
            <span className={local.crumbSep}>›</span>
            <span className={local.crumbCurrent}>{scope.city}</span>
          </>
        )}
        {data?.subtotal && (
          <span className={local.crumbTotal}>
            <strong>{money(data.subtotal.revenue)}</strong>
            {' across '}{count(data.subtotal.regions)}{' '}
            {grain === 'pincode' ? 'pincodes' : grain === 'city' ? 'cities' : 'states'}
          </span>
        )}
      </div>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.progress ? 'Sync in progress'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load regions'}
            </strong>
            <div>{error.message}</div>
            <SyncProgress progress={error.progress} />
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th />
                <th>Region</th>
                <th className={local.numeric}>Revenue</th>
                <th className={local.numeric}>Units</th>
                <th className={local.numeric}>Orders</th>
                <th className={local.numeric}>Share</th>
                <th className={local.numeric}>Growth</th>
                {showWholesale && (
                  <th className={local.numeric} title="Share of this region's revenue from business-to-business orders">
                    Wholesale
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className={local.emptyCell} colSpan={columns}>
                    {loading ? 'Loading…' : 'No orders in this period.'}
                  </td>
                </tr>
              ) : rows.map((row, index) => (
                <tr key={row.region}
                  className={canDrill && !row.unassigned ? local.drillable : undefined}
                  onClick={canDrill ? () => drill(row) : undefined}>
                  <td className={local.rankCell}>{index + 1}</td>
                  <td className={row.unassigned ? local.unassigned : undefined}>
                    {row.region}
                    {canDrill && !row.unassigned && (
                      <FiChevronRight className={local.drillHint} />
                    )}
                  </td>
                  <td className={local.numeric}>{money(row.revenue)}</td>
                  <td className={local.numeric}>{count(row.units)}</td>
                  <td className={local.numeric}>{count(row.orders)}</td>
                  <td className={`${local.numeric} ${local.shareCell}`}>
                    <Share value={row.share} />
                  </td>
                  <td className={local.numeric}><Growth value={row.growth} /></td>
                  {showWholesale && (
                    <td className={local.numeric}>
                      <Wholesale value={row.business_share} />
                    </td>
                  )}
                </tr>
              ))}

              {/* The tail, folded rather than truncated, so the column still
                  adds up to the total below it. */}
              {data?.remainder && (
                <tr className={local.remainder}>
                  <td />
                  <td>{data.remainder.region} ({count(data.remainder.regions)})</td>
                  <td className={local.numeric}>{money(data.remainder.revenue)}</td>
                  <td className={local.numeric}>{count(data.remainder.units)}</td>
                  <td className={local.numeric}>{count(data.remainder.orders)}</td>
                  <td className={local.numeric}>
                    {data.remainder.share === null ? <Blank />
                      : `${(data.remainder.share * 100).toFixed(1)}%`}
                  </td>
                  <td className={local.numeric}><Blank /></td>
                  {showWholesale && (
                    <td className={local.numeric}>
                      <Wholesale value={data.remainder.business_share} />
                    </td>
                  )}
                </tr>
              )}

              {data?.subtotal && rows.length > 0 && (
                <tr className={local.subtotalRow}>
                  <td />
                  <td>Total</td>
                  <td className={local.numeric}>{money(data.subtotal.revenue)}</td>
                  <td className={local.numeric}>{count(data.subtotal.units)}</td>
                  <td className={local.numeric}>{count(data.subtotal.orders)}</td>
                  <td className={local.numeric} />
                  <td className={local.numeric} />
                  {showWholesale && <td className={local.numeric} />}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Stated beside the figures rather than left implied. */}
      {data?.coverage && (
        <div className={local.coverage}>
          {count(data.coverage.orders_with_address)} orders with a delivery
          address
          {data.coverage.orders_without_address > 0 && (
            <>, {count(data.coverage.orders_without_address)} without — those
            appear as Unassigned rather than being dropped</>
          )}
          {data.coverage.export_orders_excluded > 0 && (
            <>, {count(data.coverage.export_orders_excluded)} export orders
            excluded</>
          )}
          {data.coverage.unnormalised_cities > 0 && (
            <>, {count(data.coverage.unnormalised_cities)} city names left as
            typed because their orders carry no pincode</>
          )}
          {data.coverage.conflicting_pincodes > 0 && (
            <>, {count(data.coverage.conflicting_pincodes)} pincodes filed under
            more than one state</>
          )}. {data.coverage.note}
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RegionTable;
